import { type ComponentProps } from "react";
import { cn } from "~/lib/utils";
import { eventColor } from "./EventSwatch";
import { formatDate, shortenLabel } from "./helpers";
import { activateOnKey } from "./keyboard";
import { type AnnualWheelEvent } from "./types";
import { type ChartTooltipState } from "./useChartTooltip";

// From the tip of a pin to the middle of its head
export const EVENT_PIN_HEIGHT = 18;
// The room the head of a pin takes up next to another one
export const EVENT_PIN_HEAD_GAP = 14;
// The room the label of a pin takes up above or below another one
export const EVENT_LABEL_LINE_HEIGHT = 14;

const HEAD_RADIUS = 6;
const ACTIVE_HEAD_RADIUS = 7.5;
const HIT_RADIUS = 11;
// A longer name is cut short; the tooltip and the list give it in full
const MAX_LABEL_LENGTH = 24;
// Of an average character at the size of the labels, a little on the wide side
const LABEL_CHARACTER_WIDTH = 6.5;
// Between the line from the head of the pin and the text
const LABEL_OFFSET = 4;

// The room the longest label takes up, from the end of its line
export const EVENT_LABEL_ROOM =
  MAX_LABEL_LENGTH * LABEL_CHARACTER_WIDTH + LABEL_OFFSET;

const eventLabel = ({ name }: AnnualWheelEvent) =>
  shortenLabel(name, MAX_LABEL_LENGTH);

// An estimate, good enough to keep labels out of each other's way
export const eventLabelWidth = (annualWheelEvent: AnnualWheelEvent) =>
  eventLabel(annualWheelEvent).length * LABEL_CHARACTER_WIDTH + LABEL_OFFSET;

type Point = [number, number];

interface Props {
  annualWheelEvent: AnnualWheelEvent;
  // Where the pin stands, and where its head is once its neighbours have made room
  tip: Point;
  head: Point;
  // An active pin draws a line from its tip to here, across what goes on that day
  guideEnd: Point;
  // Where the line from the head to the label ends, for a pin that has a label
  labelPoint?: Point;
  isHighlighted: boolean;
  tooltip: ChartTooltipState<AnnualWheelEvent>;
  onOpen: () => void;
}

export const EventPin = ({
  annualWheelEvent,
  tip: [tipX, tipY],
  head: [headX, headY],
  guideEnd: [guideX, guideY],
  labelPoint,
  isHighlighted,
  tooltip,
  onOpen,
}: Props) => {
  const isActive =
    isHighlighted || tooltip.active?.datum.id === annualWheelEvent.id;

  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={`${annualWheelEvent.name}: ${formatDate(annualWheelEvent.date)}`}
      className={cn(
        "group cursor-pointer outline-none",
        eventColor[annualWheelEvent.color],
      )}
      onClick={onOpen}
      onKeyDown={activateOnKey(onOpen)}
      {...tooltip.markProps(annualWheelEvent)}
    >
      {isActive && (
        <line
          x1={tipX}
          y1={tipY}
          x2={guideX}
          y2={guideY}
          strokeWidth={1.5}
          className="pointer-events-none stroke-current"
        />
      )}
      <line
        x1={tipX}
        y1={tipY}
        x2={headX}
        y2={headY}
        strokeWidth={1.5}
        className="stroke-current"
      />
      {labelPoint && (
        <line
          x1={headX}
          y1={headY}
          x2={labelPoint[0]}
          y2={labelPoint[1]}
          className="stroke-current"
        />
      )}
      <circle
        cx={headX}
        cy={headY}
        r={HIT_RADIUS}
        className="fill-transparent"
      />
      <circle
        cx={headX}
        cy={headY}
        r={isActive ? ACTIVE_HEAD_RADIUS : HEAD_RADIUS}
        strokeWidth={2}
        className="stroke-card group-focus-visible:stroke-ring fill-current"
      />
    </g>
  );
};

interface PinLabelProps extends ComponentProps<"text"> {
  // Where the line from the pin ends; the text runs on from there
  point: Point;
  // Runs to the left, ending at the point, instead of to the right
  isFlipped: boolean;
}

// Outlined in the colour of the surface, so that a line it crosses passes behind it
export const PinLabel = ({
  point: [x, y],
  isFlipped,
  className,
  ...props
}: PinLabelProps) => (
  <text
    x={x + (isFlipped ? -LABEL_OFFSET : LABEL_OFFSET)}
    y={y}
    textAnchor={isFlipped ? "end" : "start"}
    dominantBaseline="central"
    strokeWidth={4}
    className={cn(
      "fill-foreground stroke-card text-xs [paint-order:stroke] [stroke-linejoin:round]",
      className,
    )}
    {...props}
  />
);

interface EventPinLabelProps {
  annualWheelEvent: AnnualWheelEvent;
  point: Point;
  isFlipped: boolean;
  tooltip: ChartTooltipState<AnnualWheelEvent>;
  onOpen: () => void;
}

// Drawn after all the pins, so that none of their lines runs over a label
export const EventPinLabel = ({
  annualWheelEvent,
  point,
  isFlipped,
  tooltip,
  onOpen,
}: EventPinLabelProps) => (
  <PinLabel
    point={point}
    isFlipped={isFlipped}
    aria-hidden
    className="cursor-pointer"
    onClick={onOpen}
    {...tooltip.markProps(annualWheelEvent)}
  >
    {eventLabel(annualWheelEvent)}
  </PinLabel>
);
